import * as React from 'react'
import { Heading, View, Text, useTheme, Flex, Button, Link, Alert } from '@aws-amplify/ui-react'
import { BiCopy, BiLink } from "react-icons/bi"
import { VpnWithMetrics } from '../pages/Home'
import { useLocation, useNavigate } from 'react-router-dom'
import { InfrastructureProvider, InstanceType, PricingRate } from '@mytmpvpn/mytmpvpn-common/models/location'
import { MyTmpVpnError } from '@mytmpvpn/mytmpvpn-common/errors'
import { useComponentSize } from './ResponsiveSize'


export function titleToId(children: any): string | undefined {
  const result = children.replace(/\W/g, '').toLowerCase()
  return result
}


export function copyToClipboard(text: any) {
  navigator.clipboard.writeText(text)
}

export function Scrollable({ children }: { children: any }) {
  const { pathname, hash, key } = useLocation()

  React.useEffect(() => {
    // if not a hash link, scroll to top
    if (hash === '') {
      window.scrollTo(0, 0)
    }
    // else scroll to id
    else {
      setTimeout(() => {
        const id = hash.replace('#', '')
        const element = document.getElementById(id)
        if (element) {
          element.scrollIntoView()
        }
      }, 0)
    }
  }, [pathname, hash, key]) // do this on route change


  return <View>
    {children}
  </View>
}


export function RefreshButton({ isLoading, onClick, disabled }: { isLoading: any, onClick: any, disabled: boolean }) {
  return (
    <Button disabled={disabled} isLoading={isLoading} onClick={onClick}>
      ↻ Refresh
    </Button>
  )
}

export function Linkable({ id, children }: { id: string | undefined, children: any }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const url = pathname + '#' + id

  return id ?
    <View><Flex justifyContent={'center'} alignItems={'center'} gap={'0.2rem'}>{children}<Button padding='0.2rem' margin='0.2rem' size='small' onClick={() => navigate(url)}><BiLink /></Button></Flex></View> :
    <View>{children}</View>
}


// On mobile, shift all heading levels down by 1 to fit the narrower viewport
function useResponsiveLevel(desktopLevel: 1 | 2 | 3 | 4 | 5 | 6): 1 | 2 | 3 | 4 | 5 | 6 {
  const componentSize = useComponentSize()
  if (componentSize === 'short') {
    return Math.min(desktopLevel + 1, 6) as 1 | 2 | 3 | 4 | 5 | 6
  }
  return desktopLevel
}

export function MainTitle({ children }: { children: any }) {
  const level = useResponsiveLevel(1)
  return (
    <Scrollable>
      <Heading textAlign={'center'} level={level} paddingBlock={"1rem"} id={titleToId(children)}>
        <Linkable id={titleToId(children)}>
          {children}
        </Linkable>
      </Heading>
    </Scrollable>
  )
}

export function MainSubTitle({ children }: { children: any }) {
  const level = useResponsiveLevel(2)
  return (
    <Scrollable>
      <Heading textAlign={'center'} level={level} padding={"1rem"} id={titleToId(children)}>
        <Linkable id={titleToId(children)}>
          {children}
        </Linkable>
      </Heading>
    </Scrollable>
  )
}


export function TitleSection({ children }: { children: any }) {
  const level = useResponsiveLevel(3)
  return (
    <Scrollable>
      <Heading textAlign={'left'} level={level} paddingBlock={"0.75rem"} id={titleToId(children)}>
        <Linkable id={titleToId(children)}>
          {children}
        </Linkable>
      </Heading>
    </Scrollable>
  )
}

export function TitleSubSection({ children }: { children: any }) {
  const level = useResponsiveLevel(4)
  return (
    <Scrollable>
      <Heading textAlign={'left'} level={level} paddingBlock={"0.5rem"} id={titleToId(children)}>
        <Linkable id={titleToId(children)}>
          {children}
        </Linkable>
      </Heading>
    </Scrollable>
  )
}

export function TitleSubSubSection({ children }: { children: any }) {
  const level = useResponsiveLevel(5)
  return (
    <Scrollable>
      <Heading level={level} paddingBlock={"0.5rem"} id={titleToId(children)}>
        <Linkable id={titleToId(children)}>
          {children}
        </Linkable>
      </Heading>
    </Scrollable>
  )
}

export function Copy({ children }: { children: any }) {
  const [displayCopiedMessage, setDisplayCopiedMessage] = React.useState<boolean>(false)

  return <View>
    <Button
      size='small'
      onClick={() => {
        copyToClipboard(children)
        setDisplayCopiedMessage(!displayCopiedMessage)
      }}>
      <BiCopy />
    </Button>
    {displayCopiedMessage && " Copied!"}
  </View >
}


export function Code({ children }: { children: any }) {
  const { tokens } = useTheme()

  return (
    <View
      padding={tokens.space.xs}
      backgroundColor={tokens.colors.background.tertiary}>
      <Flex direction='row' justifyContent={'space-between'}>
        <Text fontFamily={'monospace'}>
          {children}
        </Text>
        <Copy children={children} />
      </Flex>
    </View>
  )
}

export function WideView({ children }: { children: any }) {
  return (
    <View width={"100%"} textAlign={"justify"}>
      {children}
    </View>
  )
}

export function ShortCenteredTextView({ children }: { children: any }) {
  return (
    <Flex direction={'column'} alignItems={'center'}  >
      {children}
    </Flex>
  )
}

export function PageContainer({ children }: { children: any }) {
  return (
    <Flex direction="column" width="100%" marginInline="auto" gap="1.5rem" alignItems="stretch" textAlign="left"
      style={{ minWidth: 0, overflow: 'hidden' }}>
      {children}
    </Flex>
  )
}

export function Emph({ children }: { children: any }) {
  return (
    <u>{children}</u>
  )
}

export function Ref({ children }: { children: any }) {
  return <Link href={children} isExternal={true}>[ref]</Link>
}

export function HelpButton({ help, setHelp }: { help: boolean, setHelp: any }) {
  return <Button
    colorTheme='info'
    size='small'
    style={{
      fontSize: '0.5rem',
      cursor: 'help',
    }}
    onClick={() => setHelp(!help)}><Text fontSize={'0.5rem'}>?</Text>
  </Button>
}

export function Help({ helpComponent, children, initialState }: {
  helpComponent: any,
  children: any,
  initialState?: 'expanded' | 'collapsed',
}) {
  const [help, setHelp] = React.useState<boolean>(initialState === 'expanded')

  // Collapse when initialState changes to 'collapsed' (e.g., data arrived)
  React.useEffect(() => {
    if (initialState === 'collapsed') {
      setHelp(false)
    }
  }, [initialState])

  return <View>
    <View style={{ textAlign: 'center' }}>
      <Button
        size='small'
        variation="link"
        data-expanded={help}
        style={{
          fontSize: '0.75em',
          cursor: 'help',
          ...(help ? {
            backgroundColor: 'var(--amplify-colors-background-secondary)',
            border: '1px solid var(--amplify-colors-border-focus)',
          } : {}),
        }}
        padding={'0.3rem'}
        onClick={() => setHelp(!help)}>{children}
      </Button>
    </View>
    {help && helpComponent}
  </View >
}


export function HelpHelper({ helpMessage, children }: { helpMessage: any, children: any }) {
  return <Help helpComponent={
    <Text fontSize={'0.75rem'} marginTop={'0.5rem'}>
      {helpMessage}
    </Text>}>
    {children}
  </Help >
}

export function reactUpdateVpnWithMetrics(vpnsWithMetrics: VpnWithMetrics[], vpnWithMetrics: VpnWithMetrics) {
  return vpnsWithMetrics.map(vpnWM =>
    vpnWM.vpnId === vpnWithMetrics.vpnId ?
      vpnWithMetrics :
      vpnWM
  )
}

export function convertSecondsToHMSShort(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts: string[] = []
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  if (s > 0 && h === 0) parts.push(`${s}s`)
  return parts.join('') || '0s'
}

export function convertSecondsToHMSLong(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts: string[] = []
  if (h > 0) parts.push(h > 1 ? `${h} hours` : `${h} hour`)
  if (m > 0) {
    if (h > 0) parts.push(` `) // Add a space if hours are displayed
    parts.push(m > 1 ? `${m} minutes` : `${m} minute`)
  }
  if (s > 0 && h === 0) {
    if (m > 0) parts.push(` `) // Add a space if minutes are displayed
    parts.push(s > 1 ? `${s} seconds` : `${s} second`)
  }
  return parts.join('') || '0s'
}

export function getLabelForInstanceType(instanceType: InstanceType): React.ReactNode {
  switch (instanceType) {
    case InstanceType.Ultra:
      return "🚀 Ultra"
    case InstanceType.Fast:
      return "🚗 Fast"
    case InstanceType.Eco:
      return "🌱 Eco"
    default:
      return <Alert>Unknown Instance Type</Alert>
  }
}

export function getBandwidthForInstanceType(provider: InfrastructureProvider, instanceType: InstanceType): Number {
  switch (provider) {
    case InfrastructureProvider.Aws:
      switch (instanceType) {
        case InstanceType.Ultra:
          return 128
        case InstanceType.Fast:
          return 32
        case InstanceType.Eco:
          return 3
        default:
          throw new MyTmpVpnError(`Unknown instanceType: ${instanceType} for provider: ${provider}`)
      }
    default:
      throw new MyTmpVpnError(`Unsupported provider: ${provider}`)
  }
}

export function getBandwidthLabelForInstanceType(provider: InfrastructureProvider, instanceType: InstanceType): React.ReactNode {
  return `Up to ${getBandwidthForInstanceType(provider, instanceType)} Mb/s`
}

export const SORT_ORDER_ICONS = {
  asc: '↑',
  desc: '↓'
}

export interface EstimatedCostRange {
  min: number  // peanuts
  max: number  // peanuts
}

export interface EstimatedRuntimeRange {
  minSeconds: number
  maxSeconds: number
}

export function computeEstimatedCost(
  deleteAfter: number,       // seconds
  pricingRate: PricingRate
): EstimatedCostRange {
  const minutes = deleteAfter / 60
  return {
    min: pricingRate.peanutsPerMinuteMin * minutes,
    max: pricingRate.peanutsPerMinuteMax * minutes
  }
}

export function computeEstimatedRuntime(
  maxPeanuts: number,
  pricingRate: PricingRate
): EstimatedRuntimeRange {
  const MAX_SECONDS = 24 * 60 * 60 // 24 hours cap

  const minMinutes = maxPeanuts / pricingRate.peanutsPerMinuteMax
  const maxMinutes = maxPeanuts / pricingRate.peanutsPerMinuteMin

  return {
    minSeconds: Math.min(minMinutes * 60, MAX_SECONDS),
    maxSeconds: Math.min(maxMinutes * 60, MAX_SECONDS)
  }
}

export function getConfiguredMaxPeanuts(configuredPeanuts: number, balance: number) {
  if (configuredPeanuts <= 0) {
    return balance
  }
  return configuredPeanuts
}

export function formatMaxPeanuts(peanuts: number, balance: number, maxPeanutsFieldMaxValue: number) {
  if (peanuts <= 0) {
    return `Unlimited (all ${balance.toFixed(0)} peanuts)`
  }
  return `${peanuts} peanuts`
}

export function formatMaxPeanutsBalance(peanuts: number, balance: number, maxPeanutsFieldMaxValue: number) {
  if (peanuts <= 0) {
    return 0
  }
  return (balance - peanuts).toFixed(2)
}